/**
 * CancelRouteModal — route annuleren vanaf de kalenderkaart.
 *
 * Reden verplicht (picklist Routes__c.Reden_Annulering__c), toelichting vrij,
 * klantmail alleen op verzoek. Zegt vooraf wat er gebeurt: stops gaan van de
 * route af en de werkorders terug naar "plannen". Verb: POST /api/routes/{id}/cancel.
 */

import { useState } from 'react';
import clsx from 'clsx';
import { useCalendarStore } from '../../stores/calendar-store';
import type { RouteListItem } from '../../lib/salesforce';

/** Gelijk aan de picklist Routes__c.Reden_Annulering__c. */
const REDENEN = [
  'Te weinig stops',
  'Chauffeur ziek',
  'Voertuig defect',
  'Weer (sneeuw/gladheid)',
  'Noodgeval',
  'Anders',
] as const;

interface CancelRouteModalProps {
  route: RouteListItem;
  onClose: () => void;
}

export function CancelRouteModal({ route, onClose }: CancelRouteModalProps) {
  const { cancelRoute, isCancelling } = useCalendarStore();
  const [reden, setReden] = useState<string>(route.stopCount === 0 ? 'Te weinig stops' : '');
  const [toelichting, setToelichting] = useState('');
  const [stuurKlantEmail, setStuurKlantEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waarschuwing, setWaarschuwing] = useState<string | null>(null);

  const naam = route.routeName || route.name;
  const stops = route.stopCount;
  const canSubmit = !!reden && !isCancelling;

  const displayDate = new Date(route.datum + 'T00:00:00').toLocaleDateString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  });

  const handleSubmit = async () => {
    if (!reden) return;
    setError(null);
    try {
      const r = await cancelRoute(route.id, route.datum, {
        reden,
        toelichting: toelichting.trim() || undefined,
        stuurKlantEmail,
      });
      if (r.scheduleCleanupFailed) {
        setWaarschuwing('Route is geannuleerd, maar de dienst in het rooster kon niet worden opgeruimd. Haal die handmatig weg.');
        return;
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Annuleren mislukt');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-start justify-between gap-4 mb-1">
          <h3 className="text-lg font-semibold text-gray-900">Route annuleren: {naam}</h3>
          <button type="button" onClick={onClose} aria-label="Sluiten" className="text-gray-400 hover:text-gray-600">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" />
            </svg>
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4 capitalize">{displayDate}</p>

        {waarschuwing ? (
          <>
            <div className="mb-4 p-3 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-800">{waarschuwing}</div>
            <div className="flex justify-end">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-md bg-gray-900 text-white hover:bg-gray-800">
                Sluiten
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={clsx('mb-4 p-3 rounded-lg border text-sm', stops > 0 ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-gray-50 border-gray-200 text-gray-600')}>
              {stops > 0 ? (
                <>
                  <span className="font-medium">{stops} stop{stops === 1 ? '' : 's'}</span> gaan van deze route af. De werkorders gaan terug naar
                  &quot;plannen&quot; en krijgen een nieuw voorstel zodra er een route in het gebied is.
                  {route.locked && ' De route is vergrendeld; de chauffeur heeft hem mogelijk al gezien.'}
                </>
              ) : (
                'Deze route heeft geen stops. Annuleren haalt hem uit de kalender en het rooster.'
              )}
            </div>

            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Reden</label>
              <select
                value={reden}
                onChange={(e) => setReden(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">Kies een reden</option>
                {REDENEN.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-500 uppercase mb-1">Toelichting (optioneel)</label>
              <textarea
                value={toelichting}
                onChange={(e) => setToelichting(e.target.value)}
                rows={2}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {stops > 0 && (
              <label className="flex items-start gap-2 mb-4 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={stuurKlantEmail}
                  onChange={(e) => setStuurKlantEmail(e.target.checked)}
                  className="mt-0.5 text-indigo-600 focus:ring-indigo-500"
                />
                <span>Klanten mailen dat de afspraak vervalt (S_RouteGeannuleerd)</span>
              </label>
            )}

            {error && <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

            <div className="flex justify-end gap-2">
              <button type="button" onClick={onClose} disabled={isCancelling} className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50">
                Terug
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-md text-white',
                  canSubmit ? 'bg-red-600 hover:bg-red-700' : 'bg-red-300 cursor-not-allowed'
                )}
              >
                {isCancelling ? 'Bezig…' : 'Route annuleren'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
